import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { logos } from '../data/mock';
import { MapPin, Mail, Clock, Linkedin } from 'lucide-react';

const Footer = () => {
  const { language, setLanguage, t } = useLanguage();

  const navLinks = [
    { path: '/', label: t('nav.home') },
    { path: '/about', label: t('nav.about') },
    { path: '/services', label: t('nav.services') },
    { path: '/ai-concierge', label: t('nav.aiConcierge') },
    { path: '/approach', label: t('nav.approach') },
    { path: '/contact', label: t('nav.contact') },
  ];

  const handleNavClick = () => {
    // Scroll to top when navigating
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#1E2A38] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div className="lg:col-span-1">
            <Link to="/" onClick={handleNavClick}>
              <img
                src={logos.dark}
                alt="HITECH GROUP"
                className="h-10 w-auto mb-6 hover:opacity-80 transition-opacity duration-200"
              />
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              {t('footer.description')}
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#0066CC] transition-colors duration-200"
              >
                <Linkedin size={18} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#6B7B8C] mb-6">
              {t('footer.quickLinks')}
            </h3>
            <nav className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={handleNavClick}
                  className="text-white/70 hover:text-white text-sm transition-colors duration-200"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#6B7B8C] mb-6">
              {t('footer.contactInfo')}
            </h3>
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-[#0066CC] mt-0.5 flex-shrink-0" />
                <span className="text-white/70 text-sm">{t('contact.info.address')}</span>
              </div>
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-[#0066CC] mt-0.5 flex-shrink-0" />
                <a
                  href="mailto:contact@hitech-group.io"
                  className="text-white/70 hover:text-white text-sm transition-colors duration-200"
                >
                  {t('contact.info.email')}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-[#0066CC] mt-0.5 flex-shrink-0" />
                <span className="text-white/70 text-sm">{t('contact.info.hours')}</span>
              </div>
            </div>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-[#6B7B8C] mb-6">
              {t('footer.legal')}
            </h3>
            <nav className="flex flex-col gap-3 mb-6">
              <Link
                to="/privacy"
                onClick={handleNavClick}
                className="text-white/70 hover:text-white text-sm transition-colors duration-200"
              >
                {t('footer.privacy')}
              </Link>
              <Link
                to="/terms"
                onClick={handleNavClick}
                className="text-white/70 hover:text-white text-sm transition-colors duration-200"
              >
                {t('footer.terms')}
              </Link>
              <Link
                to="/legal"
                onClick={handleNavClick}
                className="text-white/70 hover:text-white text-sm transition-colors duration-200"
              >
                {t('footer.legalNotice')}
              </Link>
            </nav>
            {/* Language Switcher */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors duration-200 ${
                  language === 'en'
                    ? 'bg-white text-[#1E2A38]'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLanguage('fr')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors duration-200 ${
                  language === 'fr'
                    ? 'bg-white text-[#1E2A38]'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                FR
              </button>
            </div>
          </div>
        </div>

        {/* Corporate Statement & Copyright */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-white/60 text-sm text-center mb-4">
            {t('footer.corporateStatement')}
          </p>
          <p className="text-white/40 text-xs text-center">
            {t('footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
